import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  BottomNavigation,
  BottomNavigationAction,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import { SellOutlined, SwapHorizOutlined, Warehouse } from "@mui/icons-material";
import DirectionsBoatFilledOutlinedIcon from "@mui/icons-material/DirectionsBoatFilledOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LowPriorityIcon from "@mui/icons-material/LowPriority";

import { translate } from "../../../translate/translate";
import SimulationForecastComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-forecast.component";
import SimulationInTransitComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-in-transit.component";
import SimulationPeriodComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-period.component";
import SimulationPriorityComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-priority.component";
import SimulationStockComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-stock.component";
import SimulationBomComponent from "../../../page/general/gds-simulation/simulation-tabs/simulation-bom.component";

import { Analysis } from "../../../../app/models/general/analysis";
import { AnalysisPriority } from "../../../../app/models/general/analysis-priority";
import { BaseService } from "../../../../app/services/base-service";
import { URLS } from "../../../../app/services/app-urls";

import { AuthContext } from "../../../../app/contexts/auth-context";

import { ErrorComponent } from "../../../../app/components/error/error.component";
import Loading from "../../../../app/components/loading/loading";
import DialogVersion from "../../../../app/components/dialog/dialog.component";
import { Component } from "@/app/models/general/component";

interface SelectedTabProps {
  selectedTab: number;
  analysis: Analysis;
  selectedPeriod: number | null;
  handleSelectPeriod: (week: number) => void;
  handleSelectPriority: (selected: any[]) => void;
  dataPriority: any[];
}

const SelectedTab: FC<SelectedTabProps> = ({
  selectedTab,
  analysis,
  selectedPeriod,
  handleSelectPeriod,
  handleSelectPriority,
  dataPriority,
}) => {
  switch (selectedTab) {
    case 0:
      return <SimulationForecastComponent analysis={analysis} />;
    case 1:
      return <SimulationStockComponent analysis={analysis} />;
    case 2:
      return <SimulationInTransitComponent analysis={analysis} />;
    case 3:
      return (
        <SimulationPeriodComponent
          analysis={analysis}
          handleSelectPeriod={handleSelectPeriod}
          selectedPeriod={selectedPeriod}
        />
      );
    case 4:
      return (
        <SimulationPriorityComponent
          handleSelectPriority={handleSelectPriority}
          dataPriority={dataPriority}
        />
      );
    case 5:
      return <SimulationBomComponent analysis={analysis} dataPriority={dataPriority} />;
    default:
      return <SimulationForecastComponent analysis={analysis} />;
  }
};

const GdsSimulationComponent: FC = () => {
  const service = new BaseService<Analysis>(URLS.ANALYSIS);

  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { action: analysisId } = useParams<{ action: string }>();
  const [analysis, setAnalysis] = useState<Analysis | null>((location.state as Analysis) || null);
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(analysis?.period || null);
  const [dataPriority, setDataPriority] = useState<AnalysisPriority[]>([]);
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [isReprocessingAnalysis, setIsReprocessingAnalysis] = useState<boolean>(false);
  const isReprocessingAnalysisRef = useRef(false);

  useEffect(() => {
    if (!analysis && analysisId) {
      service.getById(analysisId).then(({ data }) => {
        setAnalysis(data);
      }).catch(ErrorComponent);
    }
  }, [analysisId]);

  useEffect(() => {
    if (analysis) {
      fetchForecastComponents();
    }
  }, [analysis]);

  const fetchForecastComponents = () => {
    if (!analysis) return;
    service.clearParameters();
    service
      .getFromDetailRoute(analysis.id, "priorities")
      .then((response: any) => {
        if (response.data.length === 0) {
          setDataPriority([]);
          return;
        }
        const reorderedRows = response.data.map((item: Component, index: number) => ({
          ...item,
          priority: index + 1,
        }));
        setDataPriority(reorderedRows);
      })
      .catch(ErrorComponent)
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSelectPriority = (selected: any[]) => {
    setDataPriority(selected);
  };

  if (!analysis) {
    return <div>{translate.t("not_found")}</div>;
  }

  const handleSelectPeriod = (week: number) => {
    setSelectedPeriod(week);
  };

  const createAnalysis = (e?: React.MouseEvent): void => {
    if (isReprocessingAnalysisRef.current) return;

    isReprocessingAnalysisRef.current = true;
    setIsReprocessingAnalysis(true);

    service
      .postFromListRoute({
        analysis_id: analysis?.id,
        user_id: user.id,
        priorities: dataPriority.map((item) => {
          return {
            component_id: item?.component_id,
            priority: item.priority,
          };
        }),
        period: selectedPeriod,
      }, "reprocess-analysis")
      .then((r: any) => {
        if (e && (e.ctrlKey || e.metaKey)) {
          window.open(`/general/analysis/${r.data.analysis_id}/processing`, '_blank');
        } else {
          navigate(`/general/analysis/${r.data.analysis_id}/processing`);
        }
      })
      .catch((error) => {
        console.error("Error refreshing notifications:", error);
        isReprocessingAnalysisRef.current = false;
        setIsReprocessingAnalysis(false);
      });
  };

  const handleCancelWithReset = (): void => {
    setOpenCancelModal(true);
  };

  const handleCloseCancelModal = (result: boolean) => {
    if (typeof result === "boolean" && result) {
      service.clearParameters();
      service.deleteFromDetailRoute(analysis?.id, "reset-all")
        .then(() => {
          navigate(-1);
        });
    }
    setOpenCancelModal(false);
  };

  const handleTabChange = (_: React.ChangeEvent<unknown>, newValue: number) => {
    setSelectedTab(newValue);
  };

  const sx = {
    borderTopLeftRadius: "4px",
    borderTopRightRadius: "4px",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    flex: "0 0 auto",
    maxWidth: "220px",
  };

  return (
    <section>
      <Loading open={loading} />
      <div
        className="floating-title floating-title-item"
        style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}
      >
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon color="primary" fontSize="large" fontWeight="fontWeightBold" />
          </IconButton>
          <Typography variant={"h5"}> {translate.t("simulate_gds")}</Typography>
        </div>
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "5px" }}>
          <Button variant="outlined" color="primary" onClick={handleCancelWithReset}>
            {translate.t("cancel")}
          </Button>
          <Button
            endIcon={<ArrowForwardIcon height={16} width={16} />}
            variant="contained"
            color="primary"
            onClick={createAnalysis}
            disabled={isReprocessingAnalysis}
          >
            {translate.t("reprocess_analysis")}
          </Button>
        </div>
      </div>
      <div>
        <BottomNavigation
          sx={{
            gap: "1px",
            display: "flex",
            justifyContent: "flex-start",
            backgroundColor: "#F0F3F6",
            height: "42px",
          }}
          showLabels
          value={selectedTab}
          onChange={handleTabChange}
        >
          <BottomNavigationAction
            label={translate.t("forecast")}
            icon={<SellOutlined sx={{ transform: "scaleX(-1)" }} />}
            sx={sx}
          />
          <BottomNavigationAction
            label={translate.t("stock")}
            icon={<Warehouse />}
            sx={sx} />
          <BottomNavigationAction
            label={translate.t("in_transit")}
            icon={<DirectionsBoatFilledOutlinedIcon sx={{ transform: "scaleX(-1)" }} />}
            sx={sx}
          />
          <BottomNavigationAction
            label={translate.t("period")}
            icon={<CalendarMonthIcon sx={{ transform: "scaleX(-1)" }} />}
            sx={sx}
          />
          <BottomNavigationAction
            label={translate.t("priority")}
            icon={<LowPriorityIcon sx={{ transform: "scaleX(-1)" }} />}
            sx={sx}
          />
          <BottomNavigationAction
            label={translate.t("bom_models")}
            icon={<SwapHorizOutlined />}
            sx={sx}
          />
        </BottomNavigation>
        <SelectedTab
          selectedTab={selectedTab}
          analysis={analysis}
          selectedPeriod={selectedPeriod}
          handleSelectPeriod={handleSelectPeriod}
          handleSelectPriority={handleSelectPriority}
          dataPriority={dataPriority}
        />
      </div>

      {openCancelModal && (
        <DialogVersion
          id="dialog-cancel-analysis"
          open={openCancelModal}
          onClose={handleCloseCancelModal}
          title={translate.t("cancel_analysis_simulation.discard_simulation")}
          message={translate.t("cancel_analysis_simulation.message")}
          labelButtonCancel={translate.t("cancel_analysis_simulation.mantain_simulation")}
          labelButton={translate.t("cancel_analysis_simulation.cancel_simulation")}
          colorButton={"primary"}
        />
      )}
    </section>
  );
};

export default GdsSimulationComponent;
